import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/solid";
import {
    Box,
    Group,
    rem,
    Text,
    Transition,
    useMantineColorScheme,
    useMantineTheme,
} from "@mantine/core";
import { SVGProps, useState } from "react";
import { RemovableChip } from "./RemovableChip";

const IconChevronDown = (props: SVGProps<SVGSVGElement>) => (
    <ChevronDownIcon {...props} />
);
const IconChevronUp = (props: SVGProps<SVGSVGElement>) => (
    <ChevronUpIcon {...props} />
);
export interface FilterPillTag {
    /** Short label shown in the pill header when active, e.g. "Katowice" */
    label: string;
    /** Called when the × on this tag is clicked */
    onRemove: () => void;
}

interface FilterPillProps {
    /** Icon element rendered before the label */
    icon: React.ReactNode;
    /** Default label when nothing is selected, e.g. "Office" */
    label: string;
    /** Active value tags to show in the pill header */
    tags?: FilterPillTag[];
    /** Whether the pill controls are disabled */
    disabled?: boolean;
    /** Content rendered inside the expanded panel */
    children: React.ReactNode;
    /** Start expanded (e.g. when a value is already selected) */
    defaultOpen?: boolean;
}

export function FilterPill({
    icon,
    label,
    tags = [],
    disabled = false,
    children,
    defaultOpen = false,
}: FilterPillProps) {
    const [open, setOpen] = useState(defaultOpen || tags.length > 0);
    const theme = useMantineTheme();
    const { colorScheme } = useMantineColorScheme();
    const isDark = colorScheme === "dark";

    const isActive = tags.length > 0;
    const borderColor = isActive
        ? `var(--mantine-color-materialAmber-6)`
        : isDark
            ? theme.colors.dark[4]
            : theme.colors.gray[4];

    const bgColor = isActive
        ? isDark
            ? "color-mix(in srgb, var(--mantine-color-materialAmber-6) 10%, var(--mantine-color-dark-6))"
            : "color-mix(in srgb, var(--mantine-color-materialAmber-2) 8%, white)"
        : isDark
            ? theme.colors.dark[6]
            : theme.colors.gray[0];

    const chevronColor = isActive
        ? `var(--mantine-color-materialAmber-6)`
        : isDark
            ? theme.colors.dark[1]
            : theme.colors.gray[7];

    return (
        <Box
            style={{
                border: `1px solid ${borderColor}`,
                borderRadius: rem(8),
                overflow: "hidden",
                minWidth: rem(130),
                background: bgColor,
                transition: "border-color 0.15s, background 0.15s",
                opacity: disabled ? 0.5 : 1,
            }}
        >
            {/* ── Pill header ── */}
            <Group
                gap={4}
                px={8}
                py={5}
                wrap="wrap"
                style={{
                    cursor: disabled ? "not-allowed" : "pointer",
                    userSelect: "none",
                    minHeight: rem(30),
                }}
                onClick={() => {
                    if (!disabled) setOpen((o) => !o);
                }}
            >
                <Group gap={4} wrap="nowrap" align="center" style={{ flex: 1 }}>
                    <Box style={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
                        {icon}
                    </Box>
                    <Text
                        size="xs"
                        fw={isActive ? 600 : 400}
                        c={isActive ? "materialAmber.9" : "dimmed"}
                        style={{ lineHeight: 1 }}
                    >
                        {label}
                    </Text>
                </Group>

                {/* Active value tags */}
                {isActive && (
                    <Group gap={4} wrap="wrap" onClick={(e) => e.stopPropagation()}>
                        {tags.map((tag) => (
                            <RemovableChip key={tag.label} label={tag.label} onRemove={tag.onRemove} />
                        ))}
                    </Group>
                )}

                {/* Chevron */}
                <Box style={{ marginLeft: "auto", display: "flex", alignItems: "center" }}>
                    {open ? (
                        <IconChevronUp style={{ width: rem(16), height: rem(16), color: chevronColor }} />
                    ) : (
                        <IconChevronDown style={{ width: rem(16), height: rem(16), color: chevronColor }} />
                    )}
                </Box>
            </Group>

            {/* ── Expanded panel ── */}
            <Transition mounted={open} transition="scale-y" duration={150} timingFunction="ease">
                {(styles) => (
                    <Box
                        px={8}
                        pb={8}
                        style={{
                            ...styles,
                            borderTop: `1px solid ${borderColor}`,
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {children}
                    </Box>
                )}
            </Transition>
        </Box>
    );
}
